import { useState } from 'react'
import { useIsMobile } from '@/hooks/use-mobile'
import { authService } from '@/services/authService'
import { LoginScreenDesktop } from './LoginScreenDesktop'
import { LoginScreenMobile } from './LoginScreenMobile'
import { getDefaultLoginFormData, type LoginScreenBaseProps } from './LoginScreenBase'

/**
 * LoginScreen component
 * Renders mobile or desktop version based on screen size
 * 
 * Uses component split pattern for different layouts:
 * - Mobile: Compact layout with vertical spacing
 * - Desktop: Standard layout with horizontal spacing
 * 
 * Manages form state, validation, and submission logic
 */
const LoginScreen = () => {
  const isMobile = useIsMobile()
  const [formData, setFormData] = useState(getDefaultLoginFormData())
  const [loginMode, setLoginMode] = useState<'otp' | 'password'>('otp')
  const [otpSent, setOtpSent] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [rememberMe, setRememberMe] = useState(false)
  const [loading, setLoading] = useState(false)
  const [sendingOtp, setSendingOtp] = useState(false)
  const [error, setError] = useState('')

  const clearField = (field: 'email' | 'password' | 'otp') => {
    setFormData(prev => ({ ...prev, [field]: '' }))
  }

  const handleFormDataChange = (updates: Partial<typeof formData>) => {
    setFormData(prev => ({ ...prev, ...updates }))
    // Clear error when user starts typing
    if (error) setError('')
  }

  const handleSendOtp = async () => {
    if (!formData.email) {
      setError('Please enter your email address')
      return
    }

    setSendingOtp(true)
    setError('')

    try {
      await authService.requestOtpCode(formData.email)
      setOtpSent(true)
      setFormData(prev => ({ ...prev, otp: '' })) // Clear OTP field when resending
    } catch (err: any) {
      setError(err.message || 'Failed to send OTP code. Please try again.')
    } finally {
      setSendingOtp(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      if (loginMode === 'otp') {
        if (!otpSent) {
          setError('Please send OTP code first')
          setLoading(false)
          return
        }
        if (!formData.otp) {
          setError('Please enter the OTP code')
          setLoading(false)
          return
        }
        await authService.confirmOtpCode(formData.email, formData.otp)
      } else {
        await authService.login({ email: formData.email, password: formData.password })
      }

      // Check attestation status after login
      try {
        const { attestationService } = await import('@/services/attestationService')
        const status = await attestationService.getAttestationStatus()
        if (!status.has_attestation) {
          // Redirect to attestation if user doesn't have one
          window.location.href = '/attestation'
        } else {
          // Redirect to main screen if user has attestation
          window.location.href = '/'
        }
      } catch (error) {
        console.error('Failed to check attestation status:', error)
        // Default to main screen if attestation check fails
        window.location.href = '/'
      }
    } catch (err: any) {
      setError(err.message || 'Login failed. Please check your credentials.')
    } finally {
      setLoading(false)
    }
  }

  const handleLoginModeChange = (mode: 'otp' | 'password') => {
    setLoginMode(mode)
    setOtpSent(false)
    setFormData(prev => ({ ...prev, otp: '', password: '' }))
    setError('')
    setShowPassword(false)
  }

  const baseProps: LoginScreenBaseProps = {
    formData,
    loginMode,
    otpSent,
    showPassword,
    rememberMe,
    loading,
    sendingOtp,
    error,
    onFormDataChange: handleFormDataChange,
    onLoginModeChange: handleLoginModeChange,
    onSendOtp: handleSendOtp,
    onShowPasswordChange: setShowPassword,
    onRememberMeChange: setRememberMe,
    onClearField: clearField,
    onSubmit: handleSubmit,
  }

  // Component Split pattern - completely separate mobile and desktop rendering
  if (isMobile) {
    return <LoginScreenMobile {...baseProps} />
  }

  return <LoginScreenDesktop {...baseProps} />
}

export default LoginScreen

